import { useState, useEffect } from 'react';
import { DashboardLayout } from '@/components/Dashboard/DashboardLayout';
import { Card } from '@/components/ui/card';
import { HoverCard, HoverCardContent, HoverCardTrigger } from '@/components/ui/hover-card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { ScrollArea } from '@/components/ui/scroll-area';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import { Calendar } from '@/components/ui/calendar';
import { useAuth } from '@/contexts/AuthContext';
import { api } from '@/lib/apiClient';
import { useToast } from '@/hooks/use-toast';
import {
  CalendarDays,
  Clock,
  Play,
  CheckCircle,
  Package,
  Building2,
  GripVertical,
  ChevronLeft,
  ChevronRight,
  Search,
  Filter,
} from 'lucide-react';
import { ModuleTabs } from '@/components/ui/ModuleTabs';
import { format, addDays, startOfWeek, addWeeks, subWeeks, isSameDay } from 'date-fns';
import { es } from 'date-fns/locale';
import { OperatorAvailabilityPanel } from '@/components/Scheduling/OperatorAvailabilityPanel';

interface WorkOrder {
  id: string;
  order_number: string;
  wo_number: number;
  order_type: string;
  product_name: string;
  quantity: number;
  priority: string;
  planning_status: string;
  scheduled_date: string | null;
  customer?: { company_name: string };
  items_completed?: number;
}

const PRIORITY_COLORS: Record<string, string> = {
  urgent: 'border-l-red-500 bg-red-500/5',
  high: 'border-l-orange-500 bg-orange-500/5',
  normal: 'border-l-blue-500 bg-blue-500/5',
  low: 'border-l-gray-400 bg-gray-400/5',
};

const STATUS_CONFIG: Record<string, { label: string; color: string; icon: any }> = {
  standby: { label: 'En espera', color: 'bg-gray-500', icon: Clock },
  scheduled: { label: 'Programada', color: 'bg-blue-500', icon: CalendarDays },
  in_production: { label: 'En producción', color: 'bg-purple-500', icon: Play },
  completed: { label: 'Completada', color: 'bg-green-500', icon: CheckCircle },
};

export default function ProductionPlanner() {
  const { userRole } = useAuth();
  const { toast } = useToast();
  const [orders, setOrders] = useState<WorkOrder[]>([]);
  const [loading, setLoading] = useState(true);
  const [weekStart, setWeekStart] = useState(() => startOfWeek(new Date(), { weekStartsOn: 1 }));
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedOrder, setSelectedOrder] = useState<WorkOrder | null>(null);
  const [scheduleDialogOpen, setScheduleDialogOpen] = useState(false);
  const [selectedDate, setSelectedDate] = useState<Date | undefined>();

  const canManage = userRole === 'admin' || userRole === 'supervisor';

  const fetchOrders = async () => {
    try {
      const { data, error } = await api
        .from('work_orders')
        .select('*')
        .eq('is_active', true)
        .in('planning_status', ['standby', 'scheduled', 'in_production'])
        .order('priority', { ascending: false });

      if (error) throw error;

      const customerIds = [...new Set((data || []).map((o: any) => o.customer_id).filter(Boolean))];
      const customersRes = customerIds.length > 0
        ? await api.from('customers').select('id,company_name').in('id', customerIds)
        : { data: [] };

      const customersMap = Object.fromEntries((customersRes.data || []).map((c: any) => [c.id, c]));

      // Get items count per order
      const orderIds = (data || []).map((o: any) => o.id);
      const { data: itemsData } = orderIds.length > 0
        ? await api.from('work_order_items')
            .select('work_order_id, status')
            .in('work_order_id', orderIds)
        : { data: [] };

      const itemsCount: Record<string, number> = {};
      (itemsData || []).forEach((item: any) => {
        if (item.status === 'completed') {
          itemsCount[item.work_order_id] = (itemsCount[item.work_order_id] || 0) + 1;
        }
      });

      const enrichedData = (data || []).map((o: any) => ({
        ...o,
        customer: customersMap[o.customer_id],
        items_completed: itemsCount[o.id] || 0,
      }));

      setOrders(enrichedData);
    } catch (error: any) {
      toast({
        title: 'Error',
        description: 'No se pudieron cargar las órdenes',
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchOrders();
  }, []);

  const standbyOrders = orders.filter(o =>
    o.planning_status === 'standby' &&
    (searchTerm === '' ||
     o.product_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
     o.order_number?.toLowerCase().includes(searchTerm.toLowerCase()))
  );

  const getOrdersForDate = (date: Date) => {
    return orders.filter(o =>
      o.scheduled_date && isSameDay(new Date(o.scheduled_date), date)
    );
  };

  const days = Array.from({ length: 7 }, (_, i) => addDays(weekStart, i));

  const handleSchedule = async () => {
    if (!selectedOrder || !selectedDate) return;

    try {
      const { error } = await api
        .from('work_orders')
        .update({
          scheduled_date: format(selectedDate, 'yyyy-MM-dd'),
          planning_status: 'scheduled',
        })
        .eq('id', selectedOrder.id);

      if (error) throw error;

      toast({
        title: 'OT programada',
        description: `${selectedOrder.order_number} programada para ${format(selectedDate, 'dd/MM/yyyy')}`,
      });

      setScheduleDialogOpen(false);
      setSelectedOrder(null);
      setSelectedDate(undefined);
      fetchOrders();
    } catch (error: any) {
      toast({
        title: 'Error',
        description: error.message,
        variant: 'destructive',
      });
    }
  };

  const handleUnschedule = async (order: WorkOrder) => {
    try {
      const { error } = await api
        .from('work_orders')
        .update({
          scheduled_date: null,
          planning_status: 'standby',
        })
        .eq('id', order.id);

      if (error) throw error;

      toast({ title: 'OT devuelta a espera' });
      fetchOrders();
    } catch (error: any) {
      toast({
        title: 'Error',
        description: error.message,
        variant: 'destructive',
      });
    }
  };

  const renderOrderCard = (order: WorkOrder, showActions = true) => {
    const status = STATUS_CONFIG[order.planning_status] || STATUS_CONFIG.standby;
    const StatusIcon = status.icon;

    const cardContent = (
      <Card
        className={`p-3 border-l-4 ${PRIORITY_COLORS[order.priority] || PRIORITY_COLORS.normal} hover:shadow-md transition-shadow cursor-pointer`}
        onClick={() => {
          if (canManage && order.planning_status === 'standby') {
            setSelectedOrder(order);
            setScheduleDialogOpen(true);
          }
        }}
      >
        <div className="flex items-start justify-between gap-2">
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 mb-1">
              <Badge variant="outline" className="font-mono text-xs">
                {order.order_number}
              </Badge>
              <Badge className={`${status.color} text-white text-xs`}>
                <StatusIcon className="w-3 h-3 mr-1" />
                {status.label}
              </Badge>
            </div>
            <p className="font-medium text-sm truncate">{order.product_name}</p>
            {order.customer && (
              <p className="text-xs text-muted-foreground truncate">
                {order.customer.company_name}
              </p>
            )}
            <div className="flex items-center gap-2 mt-1 text-xs text-muted-foreground">
              <Package className="w-3 h-3" />
              <span>{order.items_completed || 0}/{order.quantity} piezas</span>
            </div>
          </div>
          {canManage && order.planning_status === 'standby' && (
            <GripVertical className="w-4 h-4 text-muted-foreground" />
          )}
        </div>
        {showActions && canManage && order.planning_status === 'scheduled' && (
          <Button
            variant="ghost"
            size="sm"
            className="mt-2 w-full text-xs"
            onClick={(e) => {
              e.stopPropagation();
              handleUnschedule(order);
            }}
          >
            Quitar de planificación
          </Button>
        )}
      </Card>
    );

    return (
      <HoverCard key={order.id} openDelay={300} closeDelay={100}>
        <HoverCardTrigger asChild>
          {cardContent}
        </HoverCardTrigger>
        <HoverCardContent className="w-80" side="right" align="start">
          <div className="space-y-3 text-sm">
            <div>
              <div className="font-semibold text-base">{order.order_number}</div>
              <div className="text-muted-foreground">{order.product_name}</div>
            </div>
            <div className="grid grid-cols-2 gap-2">
              <div>
                <span className="text-muted-foreground">Cliente:</span>
                <div className="font-medium">{order.customer?.company_name || 'Sin cliente'}</div>
              </div>
              <div>
                <span className="text-muted-foreground">Cantidad:</span>
                <div className="font-medium">{order.quantity} uds</div>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-2">
              <div>
                <span className="text-muted-foreground">Progreso:</span>
                <div className="font-medium">{order.items_completed || 0}/{order.quantity} piezas</div>
              </div>
              <div>
                <span className="text-muted-foreground">Prioridad:</span>
                <Badge variant="outline" className="ml-1 capitalize">{order.priority}</Badge>
              </div>
            </div>
            {order.operator && (
              <div>
                <span className="text-muted-foreground">Operario:</span>
                <div className="font-medium">{order.operator.full_name}</div>
              </div>
            )}
            {order.due_date && (
              <div>
                <span className="text-muted-foreground">Fecha límite:</span>
                <div className="font-medium">{new Date(order.due_date).toLocaleDateString('es-ES')}</div>
              </div>
            )}
          </div>
        </HoverCardContent>
      </HoverCard>
    );
  };

  return (
    <DashboardLayout>
      <div className="p-4 border-b">
        <ModuleTabs
          tabs={[
            { value: 'calendario', label: 'Calendario', href: '/schedule' },
            { value: 'planificador', label: 'Planificador', href: '/production-planner' },
            { value: 'gantt', label: 'Gantt', href: '/gantt-planner' },
            { value: 'kanban', label: 'Kanban', href: '/production-kanban' },
            { value: 'operarios', label: 'Operarios', href: '/operators-management' },
          ]}
        />
      </div>
      <div className="flex h-[calc(100vh-140px)]">
        {/* Panel izquierdo - OTs en espera */}
        <div className="w-80 border-r bg-muted/30 flex flex-col">
          <div className="p-4 border-b">
            <h2 className="font-semibold text-lg mb-3 flex items-center gap-2">
              <Clock className="w-5 h-5" />
              En Espera ({standbyOrders.length})
            </h2>
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
              <Input
                placeholder="Buscar OT..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-9"
              />
            </div>
          </div>
          <ScrollArea className="flex-1 p-4">
            <div className="space-y-3">
              {standbyOrders.length === 0 ? (
                <p className="text-sm text-muted-foreground text-center py-8">
                  No hay OTs en espera
                </p>
              ) : (
                standbyOrders.map(order => renderOrderCard(order, false))
              )}
            </div>
          </ScrollArea>
        </div>

        {/* Panel derecho - Calendario semanal */}
        <div className="flex-1 flex flex-col">
          <div className="p-4 border-b flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Button variant="outline" size="icon" onClick={() => setWeekStart(subWeeks(weekStart, 1))}>
                <ChevronLeft className="w-4 h-4" />
              </Button>
              <Button variant="outline" size="icon" onClick={() => setWeekStart(addWeeks(weekStart, 1))}>
                <ChevronRight className="w-4 h-4" />
              </Button>
              <Button variant="outline" onClick={() => setWeekStart(startOfWeek(new Date(), { weekStartsOn: 1 }))}>
                Hoy
              </Button>
            </div>
            <h2 className="font-semibold text-lg">
              {format(weekStart, "d 'de' MMMM", { locale: es })} - {format(addDays(weekStart, 6), "d 'de' MMMM yyyy", { locale: es })}
            </h2>
            <div className="w-32" />
          </div>

          <div className="flex-1 overflow-auto">
            <div className="grid grid-cols-7 h-full min-h-[600px]">
              {days.map((day, idx) => {
                const dayOrders = getOrdersForDate(day);
                const isToday = isSameDay(day, new Date());

                return (
                  <div
                    key={idx}
                    className={`border-r last:border-r-0 flex flex-col ${isToday ? 'bg-primary/5' : ''}`}
                  >
                    <div className={`p-2 border-b text-center ${isToday ? 'bg-primary/10' : 'bg-muted/50'}`}>
                      <p className="text-xs text-muted-foreground uppercase">
                        {format(day, 'EEE', { locale: es })}
                      </p>
                      <p className={`text-lg font-semibold ${isToday ? 'text-primary' : ''}`}>
                        {format(day, 'd')}
                      </p>
                    </div>
                    <ScrollArea className="flex-1 p-2">
                      <div className="space-y-2">
                        {dayOrders.map(order => renderOrderCard(order))}
                      </div>
                    </ScrollArea>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </div>

      {/* Dialog para programar OT */}
      <Dialog open={scheduleDialogOpen} onOpenChange={setScheduleDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Programar Orden de Trabajo</DialogTitle>
          </DialogHeader>
          {selectedOrder && (
            <div className="space-y-4">
              <div className="p-3 bg-muted rounded-lg">
                <p className="font-medium">{selectedOrder.order_number}</p>
                <p className="text-sm text-muted-foreground">{selectedOrder.product_name}</p>
                <p className="text-sm">{selectedOrder.quantity} piezas</p>
              </div>
              <div>
                <p className="text-sm font-medium mb-2">Seleccionar fecha de inicio:</p>
                <Calendar
                  mode="single"
                  selected={selectedDate}
                  onSelect={setSelectedDate}
                  locale={es}
                  className="rounded-md border"
                />
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setScheduleDialogOpen(false)}>
              Cancelar
            </Button>
            <Button onClick={handleSchedule} disabled={!selectedDate}>
              Programar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </DashboardLayout>
  );
}
